import { Skeleton, Modal, message } from "antd";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import baseUrl from "../../../../utils/baseUrl";
import {
	clearTicket,
	loadSingleTicket,
} from "../../../../redux/rtk/features/crm/ticket/ticketSlice";
import Attachment from "./Comment/Attachment";
import TicketInformation from "./TicketInformation";
import TicketStatusUpdate from "./TicketStatusUpdate";
import {
	IoArrowBack,
	IoCalendarOutline,
	IoPersonOutline,
	IoImagesOutline,
	IoTrashOutline,
} from "react-icons/io5";
import "./DetailsPage.css";

// Status names are free text (set up in Ticket Status setup), so match on
// keywords rather than an exact enum to color the badge sensibly either way.
const getStatusTone = (statusName = "") => {
	const name = statusName.toLowerCase();
	if (name.includes("approve") || name.includes("resolve")) return "is-approved";
	if (name.includes("pending")) return "is-pending";
	if (name.includes("progress")) return "is-progress";
	if (name.includes("reject") || name.includes("cancel")) return "is-rejected";
	return "";
};

const TicketDetails = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams("id");
	const { ticket, loading } = useSelector((state) => state.ticket);

	useEffect(() => {
		dispatch(loadSingleTicket(id));
		return () => {
			dispatch(clearTicket());
		};
	}, [dispatch, id]);

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete this post?",
			content: "This action can't be undone.",
			okText: "Delete",
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					const response = await fetch(`${baseUrl}/ticket/${id}`, {
						method: "DELETE",
					});
					if (response.ok) {
						message.success("Post deleted successfully");
						navigate("/support/ticket");
					} else {
						message.error("Failed to delete the post.");
					}
				} catch (error) {
					message.error("An error occurred: " + error.message);
				}
			},
		});
	};

	return (
		<div className='ticket-details-page'>
			<div className='ticket-details-shell'>
				<button
					type='button'
					className='ticket-details-back'
					onClick={() => navigate(-1)}
				>
					<IoArrowBack /> Back to Posts
				</button>

				<div className='ticket-details-header'>
					<div className='ticket-details-header-top'>
						<span className='ticket-details-id'>Post #{ticket?.ticketId}</span>
						{ticket?.ticketStatus?.ticketStatusName && (
							<span
								className={`ticket-details-status-tag ${getStatusTone(
									ticket.ticketStatus.ticketStatusName
								)}`}
							>
								{ticket.ticketStatus.ticketStatusName}
							</span>
						)}
					</div>

					<h1 className='ticket-details-title'>{ticket?.subject}</h1>

					<div className='ticket-details-meta'>
						<span className='ticket-details-meta-item'>
							<IoPersonOutline /> {ticket?.customer?.fullName || "No Name"}
						</span>
						{ticket?.createdAt && (
							<span className='ticket-details-meta-item'>
								<IoCalendarOutline /> {dayjs(ticket.createdAt).format("MMM D, YYYY")}
							</span>
						)}
						<TicketInformation ticket={ticket} />
					</div>

					<div className='ticket-details-actions'>
						{ticket && <TicketStatusUpdate />}
						<button
							type='button'
							onClick={handleDelete}
							className='ticket-details-delete'
						>
							<IoTrashOutline /> Delete
						</button>
					</div>
				</div>

				<div className='ticket-details-body'>
					<Skeleton loading={loading} active>
						<div
							className='ticket-details-description'
							dangerouslySetInnerHTML={{ __html: ticket?.description || "" }}
						/>

						{ticket?.images?.length > 0 && (
							<div className='ticket-details-attachments'>
								<h4 className='ticket-details-attachments-title'>
									<IoImagesOutline /> Attachments
								</h4>
								<Attachment attachments={ticket.images} />
							</div>
						)}
					</Skeleton>
				</div>
			</div>
		</div>
	);
};
export default TicketDetails;
